import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { t } from '@/i18n/translations';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = localStorage.getItem('sop_raft_lang') || 'pt-BR';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      toast.success(t('success', lang));
      const returnTo = searchParams.get('returnTo');
      navigate(returnTo && returnTo.startsWith('/') ? returnTo : '/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      let message = t('error', lang);
      if (typeof detail === 'string') message = detail;
      else if (Array.isArray(detail)) message = detail.map(e => e.msg || JSON.stringify(e)).join(' ');
      setError(message);
      toast.error(message);
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-950">{t('app_name', lang)}</h1>
          <p className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-1 font-semibold">{t('app_subtitle', lang)}</p>
        </div>
        <div className="bg-white rounded-sm border border-zinc-200 shadow-sm p-8">
          <h2 className="text-xl font-bold text-zinc-950 mb-1">{t('welcome_back', lang)}</h2>
          <p className="text-sm text-zinc-500 mb-6">{t('enter_credentials', lang)}</p>

          {/* Google Sign-In Button */}
          <button onClick={handleGoogleLogin} data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300 transition-all mb-5 font-medium text-sm text-zinc-700">
            <GoogleIcon />
            {lang === 'en' ? 'Sign in with Google' : lang === 'es' ? 'Iniciar sesión con Google' : 'Entrar com Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs text-zinc-400 font-medium uppercase">{lang === 'en' ? 'or' : lang === 'es' ? 'o' : 'ou'}</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm" data-testid="login-error">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('email', lang)}</Label>
              <Input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required placeholder={t('email_placeholder', lang)} className="mt-1.5" data-testid="login-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('password', lang)}</Label>
              <Input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required placeholder={t('your_password', lang)} className="mt-1.5" data-testid="login-password" />
              <div className="text-right mt-1.5">
                <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-950 hover:underline font-medium" data-testid="forgot-password-link">
                  {lang === 'en' ? 'Forgot my password' : lang === 'es' ? 'Olvidé mi contraseña' : 'Esqueci minha senha'}
                </Link>
              </div>
            </div>
            <Button type="submit" className="w-full bg-zinc-950 text-white hover:bg-zinc-800" disabled={loading} data-testid="login-submit">
              {loading ? '...' : t('login', lang)}
            </Button>
            <div className="text-center text-sm text-zinc-600">
              {t('no_account', lang)}{' '}
              <Link to="/register" className="text-zinc-950 font-semibold hover:underline">{t('register_link', lang)}</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
